export interface AiTokenLimitRealtimeEventData {
  type?: string;
  meta_data?: string;
  user_id?: string;
  [key: string]: unknown;
}

export interface AiTokenLimitDialogViewModel {
  usagePercent: number;
  licenseName: string;
  tokensTrancheSizeFormatted: string;
}

const USAGE_PERCENT_KEYS = [
  'usage',
] as const;

const LICENSE_NAME_KEYS = ['license_name'] as const;

const TRANCHE_SIZE_KEYS = [
  'limit',
] as const;

function parseEventMeta(
  data: AiTokenLimitRealtimeEventData | null | undefined
): Record<string, unknown> {
  if (!data) {
    return {};
  }

  const rawMeta = data.meta_data;
  if (typeof rawMeta === 'string' && rawMeta.trim()) {
    try {
      const parsed = JSON.parse(rawMeta);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }

  return data;
}

function readFirstNumber(source: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = Number(source[key]);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function readFirstString(source: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = String(source[key] ?? '').trim();
    if (value) {
      return value;
    }
  }
  return '';
}

function normalizeUsagePercent(value: number | null): number {
  if (value == null || !Number.isFinite(value)) {
    return 80;
  }
  if (value > 0 && value <= 1) {
    return Math.round(value * 100);
  }
  return Math.round(value);
}

function formatTokenCount(value: number): string {
  return value.toLocaleString('en-US');
}

export function buildAiTokenLimitDialogViewModel(
  data: AiTokenLimitRealtimeEventData | null | undefined
): AiTokenLimitDialogViewModel {
  const meta = parseEventMeta(data);
  const usagePercent = normalizeUsagePercent(readFirstNumber(meta, USAGE_PERCENT_KEYS));
  const licenseName = readFirstString(meta, LICENSE_NAME_KEYS) || 'your license';
  const trancheSize = readFirstNumber(meta, TRANCHE_SIZE_KEYS) ?? 5_000_000;

  return {
    usagePercent,
    licenseName,
    tokensTrancheSizeFormatted: formatTokenCount(5000000),
  };
}
